import { useState, useCallback, useEffect, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight, Maximize2, Volume2, VolumeX, Youtube } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Reveal } from '@/components/ui/reveal';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import LocaleLink from '@/components/LocaleLink';

import dashboardVideo from '@/assets/tour/tour-dashboard.mp4.asset.json';
import dashboardPoster from '@/assets/tour/tour-dashboard-poster.jpg.asset.json';
import controlsVideo from '@/assets/tour/tour-controls.mp4.asset.json';
import controlsPoster from '@/assets/tour/tour-controls-poster.jpg.asset.json';
import workflowsVideo from '@/assets/tour/tour-workflows.mp4.asset.json';
import workflowsPoster from '@/assets/tour/tour-workflows-poster.jpg.asset.json';
import processVideo from '@/assets/tour/tour-process.mp4.asset.json';
import processPoster from '@/assets/tour/tour-process-poster.jpg.asset.json';
import documentsVideo from '@/assets/tour/tour-policies.mp4.asset.json';
import documentsPoster from '@/assets/tour/tour-policies-poster.jpg.asset.json';
import { tourPlaylistUrl } from '@/lib/localizedVideo';

interface Capability {
  labelKey: string;
  taglineKey: string;
  highlightKeys: string[];
  video: string;
  poster: string;
  youtubeId: string;
}

export const YOUTUBE_CHANNEL_URL = 'https://www.youtube.com/@csquaregrc';
const youtubeWatchUrl = (id: string) => `https://www.youtube.com/watch?v=${id}`;

const capabilityDefs: Capability[] = [
  {
    labelKey: 'platformSnapshot.dashboard',
    taglineKey: 'platformSnapshot.dashboardTagline',
    highlightKeys: ['platformSnapshot.dashboardH1', 'platformSnapshot.dashboardH2', 'platformSnapshot.dashboardH3'],
    video: dashboardVideo.url,
    poster: dashboardPoster.url,
    youtubeId: '_YVhO7HX_jM',
  },
  {
    labelKey: 'platformSnapshot.controls',
    taglineKey: 'platformSnapshot.controlsTagline',
    highlightKeys: ['platformSnapshot.controlsH1', 'platformSnapshot.controlsH2', 'platformSnapshot.controlsH3'],
    video: controlsVideo.url,
    poster: controlsPoster.url,
    youtubeId: '5K6nwkaGQTY',
  },
  {
    labelKey: 'platformSnapshot.workflows',
    taglineKey: 'platformSnapshot.workflowsTagline',
    highlightKeys: ['platformSnapshot.workflowsH1', 'platformSnapshot.workflowsH2', 'platformSnapshot.workflowsH3'],
    video: workflowsVideo.url,
    poster: workflowsPoster.url,
    youtubeId: 'V7F1CYmgluA',
  },
  {
    labelKey: 'platformSnapshot.process',
    taglineKey: 'platformSnapshot.processTagline',
    highlightKeys: ['platformSnapshot.processH1', 'platformSnapshot.processH2', 'platformSnapshot.processH3'],
    video: processVideo.url,
    poster: processPoster.url,
    youtubeId: 'YeXna-lh6sQ',
  },
  {
    labelKey: 'platformSnapshot.documents',
    taglineKey: 'platformSnapshot.documentsTagline',
    highlightKeys: ['platformSnapshot.documentsH1', 'platformSnapshot.documentsH2', 'platformSnapshot.documentsH3'],
    video: documentsVideo.url,
    poster: documentsPoster.url,
    youtubeId: 'PYQ5j_HXM0E',
  },
];

const PlatformSnapshot = () => {
  const { t, i18n } = useTranslation();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [carouselMuted, setCarouselMuted] = useState(true);
  const activeCap = lightboxIndex === null ? null : capabilityDefs[lightboxIndex];


  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi || lightboxIndex !== null) return;
    const interval = setInterval(() => emblaApi.scrollNext(), 20000);
    return () => clearInterval(interval);
  }, [emblaApi, lightboxIndex]);

  // Only the visible clip plays. Respect reduced-motion by leaving posters in place.
  useEffect(() => {
    const reduceMotion = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    videoRefs.current.forEach((video, index) => {
      if (!video) return;
      video.muted = carouselMuted || index !== selectedIndex;
      if (index === selectedIndex && !reduceMotion && lightboxIndex === null) {
        if (video.preload !== 'auto') {
          video.preload = 'auto';
          if (video.readyState === 0) video.load();
        }
        const attempt = video.play();
        if (attempt && typeof attempt.catch === 'function') attempt.catch(() => undefined);
      } else {
        video.pause();
      }
    });
  }, [selectedIndex, lightboxIndex, carouselMuted]);

  const videoSchema = capabilityDefs.map((cap) => ({
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: `cSquare GRC Tour: ${t(cap.labelKey)}`,
    description: t(cap.taglineKey),
    thumbnailUrl: [cap.poster, `https://i.ytimg.com/vi/${cap.youtubeId}/maxresdefault.jpg`],
    contentUrl: cap.video,
    embedUrl: `https://www.youtube.com/embed/${cap.youtubeId}`,
    url: youtubeWatchUrl(cap.youtubeId),
    // uploadDate deliberately absent. It was hardcoded to 2026-08-01 for every
    // clip here as well as on the platform tour, which is fabricated structured
    // data served to Google. Real dates come from YouTube Studio; the property
    // is optional, so omitting it costs a little rich-result eligibility and
    // nothing in honesty.
    publisher: {
      '@type': 'Organization',
      name: 'cSquare GRC',
      url: 'https://csquaregrc.com',
    },
  }));

  return (
    <section className="py-12 bg-secondary/30 relative overflow-hidden">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(videoSchema) }}
      />
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5 Q40 20 55 25 Q40 35 30 55 Q20 35 5 25 Q20 20 30 5Z' fill='none' stroke='%23000' stroke-width='0.5'/%3E%3C/svg%3E")`,
      }} />

      <div className="container mx-auto px-4 relative z-10">
        <Reveal>
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-primary uppercase tracking-wider mb-3">
              {t('platformSnapshot.eyebrow')}
            </p>
            <h2 className="section-title-fluid font-bold mb-4">
              {t('platformSnapshot.title')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('platformSnapshot.subtitle')}
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="relative max-w-5xl mx-auto group/carousel">
            <div className="overflow-hidden" ref={emblaRef}>
              <div className="flex">
                {capabilityDefs.map((cap, index) => {
                  const label = t(cap.labelKey);
                  return (
                    <div key={cap.labelKey} className="flex-[0_0_100%] min-w-0 px-2">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 items-center">
                        <div className="group/video relative w-full">
                          <button
                            type="button"
                            onClick={() => setLightboxIndex(index)}
                            aria-label={`${label} - ${t('platformSnapshot.expand')}`}
                            className="block w-full overflow-hidden border border-border shadow-lg bg-background cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <video
                              ref={(el) => { videoRefs.current[index] = el; }}
                              src={cap.video}
                              poster={cap.poster}
                              muted
                              loop
                              playsInline
                              preload={index === 0 ? 'metadata' : 'none'}
                              aria-hidden="true"
                              className="w-full h-auto aspect-video object-cover"
                            />
                            <span className="pointer-events-none absolute inset-0 bg-foreground/0 group-hover/video:bg-foreground/10 transition-colors" />
                            <span className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-background/90 border border-border px-2.5 py-1.5 text-xs font-medium text-foreground opacity-0 group-hover/video:opacity-100 transition-opacity">
                              <Maximize2 className="h-3.5 w-3.5" />
                              {t('platformSnapshot.expand')}
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setCarouselMuted((m) => !m)}
                            aria-label={carouselMuted ? t('platformSnapshot.unmute') : t('platformSnapshot.mute')}
                            aria-pressed={!carouselMuted}
                            className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 bg-background/90 border border-border px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            {carouselMuted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
                            <span>{carouselMuted ? t('platformSnapshot.unmute') : t('platformSnapshot.mute')}</span>
                          </button>
                        </div>
                        <div className="space-y-4">
                          <h3 className="text-xl md:text-2xl font-bold">{label}</h3>
                          <p className="text-muted-foreground">{t(cap.taglineKey)}</p>
                          <ul className="space-y-2">
                            {cap.highlightKeys.map((hKey) => (
                              <li key={hKey} className="flex items-start gap-2 text-sm text-muted-foreground">
                                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                                {t(hKey)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <button
              onClick={scrollPrev}
              className="absolute left-0 md:-left-12 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-background/80 backdrop-blur-sm border border-border flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity duration-200 hover:bg-background z-10"
              aria-label="Previous slide"
            >
              <ChevronLeft className="h-4 w-4 text-foreground" />
            </button>
            <button
              onClick={scrollNext}
              className="absolute right-0 md:-right-12 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-background/80 backdrop-blur-sm border border-border flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity duration-200 hover:bg-background z-10"
              aria-label="Next slide"
            >
              <ChevronRight className="h-4 w-4 text-foreground" />
            </button>

            <div className="flex justify-center gap-2 mt-8">
              {capabilityDefs.map((cap, index) => (
                <button
                  key={cap.labelKey}
                  onClick={() => scrollTo(index)}
                  className={cn(
                    'h-2 rounded-full transition-all duration-200',
                    index === selectedIndex
                      ? 'bg-primary w-6'
                      : 'bg-muted-foreground/30 w-2 hover:bg-muted-foreground/50'
                  )}
                  aria-label={`Go to ${t(cap.labelKey)}`}
                />
              ))}
            </div>

            <div className="mt-6 text-center">
              <a
                href={tourPlaylistUrl(i18n.language)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
              >
                <Youtube className="h-4 w-4" />
                {t('platformSnapshot.fullTour')}
              </a>
            </div>
          </div>
        </Reveal>
      </div>

      <Dialog open={lightboxIndex !== null} onOpenChange={(open) => !open && setLightboxIndex(null)}>
        <DialogContent className="max-w-6xl w-[95vw] p-0 border-border bg-background">
          {activeCap && (
            <>
              <DialogTitle className="sr-only">{t(activeCap.labelKey)}</DialogTitle>
              <DialogDescription className="sr-only">{t(activeCap.taglineKey)}</DialogDescription>
              <div className="relative">
                <video
                  key={activeCap.video}
                  src={activeCap.video}
                  poster={activeCap.poster}
                  controls
                  autoPlay
                  playsInline
                  className="w-full h-auto aspect-video bg-background"
                />
                <div className="absolute left-4 top-4 flex items-center gap-3 bg-background/90 border border-border px-3 py-2">
                  <span className="hidden sm:inline text-xs text-muted-foreground">
                    {t('platformSnapshot.demoCtaSub')}
                  </span>
                  <Button asChild size="sm">
                    <LocaleLink to="/book-demo" onClick={() => setLightboxIndex(null)}>
                      {t('platformSnapshot.demoCta')}
                    </LocaleLink>
                  </Button>
                </div>
              </div>
              <div className="px-6 pb-6 pt-4 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-bold">{t(activeCap.labelKey)}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{t(activeCap.taglineKey)}</p>
                </div>
                <div className="flex flex-col gap-3 sm:grid sm:w-fit sm:grid-flow-col sm:auto-cols-fr">
                  <Button className="w-full" asChild variant="ghost">
                    <a
                      href={youtubeWatchUrl(activeCap.youtubeId)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Youtube className="mr-2 h-4 w-4" />
                      {t('platformSnapshot.watchOnYouTube')}
                    </a>
                  </Button>
                  <Button className="w-full" asChild variant="outline">
                    <LocaleLink to="/book-demo" onClick={() => setLightboxIndex(null)}>
                      {t('platformSnapshot.demoCta')}
                    </LocaleLink>
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default PlatformSnapshot;
